import React, { useRef } from "react";
import { gsap, ScrollTrigger, useGSAP, reduced, whenFontsReady } from "../../lib/motion";
import { QUOTE } from "../../data/cv";

/**
 * The line that lights up as you scroll past it.
 *
 * Each word is its own element carrying a `--lit` between 0 and 1, and the
 * scrub walks that value across the sentence. Two things had to be true for
 * this to work:
 *
 *   - `--lit` is registered with @property (see index.css), or the browser
 *     treats it as a string and flips 0 to 1 halfway with no interpolation;
 *   - the words are real elements, not a gradient clipped to text. A clipped
 *     gradient was the first attempt and it forces the browser to re-raster
 *     the whole block every frame, which stutters on a phone. Fifteen elements
 *     each changing one colour does not.
 *
 * There is no SplitText here on purpose: the words come from the data file
 * already, so splitting would mean measuring text that React can just render
 * pre-split — and nothing here depends on where the LINES break, only the
 * words, so it does not have to wait on font metrics to be correct. It waits
 * anyway before creating the trigger, because the block's height changes when
 * Unbounded swaps in and a stale start position is a section that lights up
 * at the wrong time.
 */
export default function Quote() {
  const root = useRef(null);

  useGSAP(
    () => {
      const words = gsap.utils.toArray(".word", root.current);
      if (!words.length || reduced()) return;

      gsap.set(words, { "--lit": 0 });

      whenFontsReady().then(() => {
        /* useGSAP's context has already reverted if the component unmounted
           while the fonts were loading — bail rather than leaking a trigger
           nobody owns. */
        if (!root.current?.isConnected) return;

        gsap.to(words, {
          "--lit": 1,
          ease: "none",
          stagger: 0.5,
          scrollTrigger: {
            trigger: root.current,
            start: "top 85%",
            /* Finishes well before the block leaves, so the last word is lit
               while you are still looking at the sentence rather than at the
               section after it. */
            end: "bottom 72%",
            scrub: 0.15,
          },
        });
        ScrollTrigger.refresh();
      });

      /* Nothing is killed here. gsap.context owns the tween and the trigger it
         created, and killing them first is exactly what stops revert() from
         restoring the inline styles it wrote. */
      return undefined;
    },
    { scope: root },
  );

  return (
    <section ref={root} className="relative py-20 desk:py-28" aria-label="Waar ik me aan hou">
      <p className="kicker mb-8">{QUOTE.by}</p>
      <p className="max-w-[20ch] font-display text-[clamp(28px,5.4vw,64px)] leading-[1.06] font-medium tracking-[-0.055em] desk:max-w-[16ch]">
        {QUOTE.text.split(" ").map((word, i) => (
          <React.Fragment key={`${word}-${i}`}>
            <span className="word">{word}</span>{" "}
          </React.Fragment>
        ))}
      </p>
    </section>
  );
}
